import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, map, of, tap } from 'rxjs';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { MyBusinessApiService } from '@app/modules/my-business/services/my-business.api.service';
import { Business } from '@app/modules/my-business/interfaces/business.interface';
import { StatementsFacade } from '@app/modules/statements/services/statements.facade';
import { groupMin } from '@app/shared/validators/group-min';

@Component({
  selector: 'sbnb-business-form',
  templateUrl: './business-form.component.html',
  styles: `
    ::ng-deep .mat-checkbox-layout {
      background-color: inherit;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessFormComponent implements OnInit {
  @Input() business: Business;
  @Input() editing = false;
  @Output() saveSuccess = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  private readonly myBusinessApiService = inject(MyBusinessApiService);
  private readonly statementsFacade = inject(StatementsFacade);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastNotificationsService);

  public loadingProperties$ = new BehaviorSubject<boolean>(true);
  public properties$ = this.statementsFacade.getPropertiesData().pipe(
    tap(() => {
      this.loadingProperties$.next(false);
    }),
    map((properties) => {
      if (!this.editing) {
        // Sort properties to put those without a business entry at the top
        return properties.sort((a, b) => {
          if (!a.business && b.business) return -1;
          if (a.business && !b.business) return 1;
          return 0;
        });
      }
      // Sort properties to put those assigned to this business at the top
      return properties.sort((a, b) => {
        if (a.business?.uuid === this.business.uuid) return -1;
        if (b.business?.uuid === this.business.uuid) return 1;
        return 0;
      });
    }),
    tap((properties) => {
      const propertiesForm = properties.reduce((acc, property) => {
        const ctrl = this.fb.control(false);
        // If the property is already assigned to another business, disable the checkbox
        if (property.business) {
          ctrl.disable();
        }

        // If we're editing and the property is already assigned to this business, enable the checkbox
        if (this.editing && property?.business && property?.business.uuid === this.business.uuid) {
          ctrl.setValue(true);
          ctrl.enable();
        }
        acc[property.id] = ctrl;
        return acc;
      }, {});
      this.businessForm.setControl('properties', this.fb.group(propertiesForm, { validators: [groupMin(1)] }));
    }),
    catchError(() => {
      this.loadingProperties$.next(false);
      return of([]);
    })
  );

  public saving$ = new BehaviorSubject<boolean>(false);

  public businessForm = this.fb.group({
    name: this.fb.control('', [Validators.required]),
    type: this.fb.control('owner', [Validators.required]),
    confirmation: this.fb.control(false, []),
    properties: this.fb.group({}, { validators: [groupMin(1)] }),
  });

  public selectedOSProperties$ = combineLatest([this.businessForm.valueChanges, this.properties$]).pipe(
    map(([value, properties]) => {
      if (value.type !== 'owner') {
        return [];
      }
      const selectedPropertyIds = Object.keys(value.properties).filter((key) => value.properties[key]);
      return properties.filter(
        (property) =>
          selectedPropertyIds.includes(String(property.id)) &&
          property.commissionRates?.length &&
          property.owners?.length
      );
    }),
    distinctUntilChanged((previous, current) => {
      return previous.length === current.length;
    }),
    tap((properties) => {
      if (properties.length) {
        this.confirmation.setValidators([Validators.requiredTrue]);
      } else {
        this.confirmation.clearValidators();
        this.confirmation.updateValueAndValidity();
      }
    })
  );

  public ngOnInit() {
    if (this.editing) {
      this.businessForm.patchValue(this.business);
    }
  }

  public get type() {
    return this.businessForm.get('type');
  }

  public get confirmation() {
    return this.businessForm.get('confirmation');
  }

  public save() {
    // Including disabled values
    const business = this.businessForm.getRawValue() as Omit<Business, 'uuid'> & { confirmation: boolean };
    const properties = Object.keys(business.properties)
      .filter((key) => Boolean(business.properties[key]))
      .map(Number);

    this.saving$.next(true);
    if (!this.editing) {
      this.myBusinessApiService.createBusiness({ ...business, properties }).subscribe(() => {
        this.saving$.next(false);
        this.toast.success('Business created successfully');
        this.router.navigate(['/operations/my-business/businesses']);
        this.saveSuccess.emit();
      });
      return;
    }

    this.myBusinessApiService.updateBusiness(this.business.uuid, { ...business, properties }).subscribe(() => {
      this.saving$.next(false);
      this.toast.success('Business updated successfully');
      this.router.navigate(['/operations/my-business/businesses']);
      this.saveSuccess.emit();
    });
  }

  public setType(type) {
    this.businessForm.get('type').setValue(type);
    this.businessForm.markAsDirty();
  }
}
